import { useEffect, useRef } from 'react';

const reviews = [
  {
    name: 'Margaret L.',
    location: 'Portland, OR',
    stars: 5,
    text: 'Kevin Hamlett writes with a rare combination of poetic grace and spiritual depth. This book stopped me in my tracks — I had to put it down and just breathe. Absolutely beautiful.',
    avatar: 'ML',
  },
  {
    name: 'David R.',
    location: 'Nashville, TN',
    stars: 5,
    text: 'I read this in one sitting and then immediately started over. The river metaphor is so perfectly sustained throughout. It feels like sitting beside a real river and having a conversation with your own soul.',
    avatar: 'DR',
  },
  {
    name: 'Sarah K.',
    location: 'Denver, CO',
    stars: 5,
    text: 'This is the kind of book that changes how you see the world. Kevin has a gift for making the profound feel accessible. I\'ve already bought copies for three friends.',
    avatar: 'SK',
  },
  {
    name: 'Thomas W.',
    location: 'Austin, TX',
    stars: 5,
    text: 'Lyrical, penetrating, and deeply moving. The Soul Line series is unlike anything else I\'ve read. Book #II deepens everything that was started in the first — I can\'t wait for what comes next.',
    avatar: 'TW',
  },
  {
    name: 'Elena M.',
    location: 'Seattle, WA',
    stars: 5,
    text: 'I found this book at exactly the right moment in my life. The words felt like they were written for me. Kevin Hamlett understands the human heart in a way that is rare and precious.',
    avatar: 'EM',
  },
  {
    name: 'James P.',
    location: 'Chicago, IL',
    stars: 5,
    text: 'Short in pages but enormous in impact. Every sentence carries weight. I\'ve highlighted more passages in this book than any other I\'ve read this year. A true gem.',
    avatar: 'JP',
  },
];

export default function ReviewsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll('.review-card');
            cards.forEach((card, i) => {
              setTimeout(() => {
                (card as HTMLElement).style.opacity = '1';
                (card as HTMLElement).style.transform = 'translateY(0)';
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    const el = sectionRef.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, []);

  return (
    <section id="reviews" className="py-24 md:py-32 bg-stone-900 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent"></div>

      <div className="max-w-7xl mx-auto px-4 md:px-10">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-amber-500/10 text-amber-400 text-xs font-semibold mb-4 border border-amber-500/20 tracking-widest uppercase">
            Reader Reviews
          </span>
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-white mb-4">
            What Readers Are{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-amber-500">
              Saying
            </span>
          </h2>
          <p className="text-stone-400 text-lg max-w-xl mx-auto">
            Words from those who have sat beside the river and been transformed.
          </p>

          {/* Overall rating */}
          <div className="flex items-center justify-center gap-3 mt-6">
            <div className="flex items-center gap-1">
              {[1,2,3,4,5].map(i => (
                <i key={i} className="ri-star-fill text-amber-400 text-xl"></i>
              ))}
            </div>
            <span className="text-white font-bold text-2xl">5.0</span>
            <span className="text-stone-500 text-sm">on Amazon Kindle</span>
          </div>
        </div>

        {/* Reviews grid */}
        <div ref={sectionRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.map((r) => (
            <div
              key={r.name}
              className="review-card group p-6 rounded-2xl bg-stone-800/60 border border-stone-700/50 hover:border-amber-500/30 transition-all duration-300"
              style={{ opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.5s ease, transform 0.5s ease' }}
            >
              {/* Stars */}
              <div className="flex items-center gap-1 mb-4">
                {Array.from({ length: r.stars }).map((_, i) => (
                  <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>
                ))}
              </div>

              {/* Quote */}
              <p className="text-stone-300 text-sm leading-relaxed mb-6 italic">
                &ldquo;{r.text}&rdquo;
              </p>

              {/* Reviewer */}
              <div className="flex items-center gap-3 pt-4 border-t border-stone-700/50">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-500/20 text-amber-300 text-xs font-bold flex-shrink-0">
                  {r.avatar}
                </div>
                <div>
                  <div className="text-white text-sm font-semibold">{r.name}</div>
                  <div className="text-stone-500 text-xs">{r.location}</div>
                </div>
                <div className="ml-auto">
                  <i className="ri-amazon-line text-stone-600 text-sm"></i>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-14 text-center">
          <a
            href="https://www.amazon.com/dp/B0GT29CWSG"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-amber-500 hover:bg-amber-400 text-stone-950 font-bold text-base transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-amazon-line text-lg"></i>
            Get Your Copy Today
          </a>
          <p className="text-stone-600 text-sm mt-4">Kindle Edition · $3.99 · Available Instantly</p>
        </div>
      </div>
    </section>
  );
}
